import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataContext } from '../../../../context/DataContext';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import ThroughputFieldsGrid from './components/ThroughputFieldsGrid';
import CreditsEngineService, { toNumber } from '../../../../services/CreditsEngineService';
import TablePaginationBar from '../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../hooks';
import HelpIcon from '../../../../components/help/HelpIcon';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

const emptyAccountForm = {
  active: true, monthly_credit_limit: '', daily_credit_limit: '',
  allow_overage: false, overage_limit: '', allow_rollover: false, rollover_limit: '',
  throughput: {}, plan_id: ''
};

const emptyUserForm = { userId: '', active: true, monthly_credit_limit: '', daily_credit_limit: '', throughput: {} };

const BillingCreditPolicyContent = () => {
  const { accountId: ownAccountId, isPlatformRoot } = useContext(DataContext);

  const [targetAccountId, setTargetAccountId] = useState(ownAccountId || '');
  useEffect(() => {
    if (ownAccountId && !targetAccountId) {
      setTargetAccountId(ownAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownAccountId]);
  const activeAccountId = targetAccountId.trim();

  const [accountPolicy, setAccountPolicy] = useState(null);
  const [userPolicies, setUserPolicies] = useState([]);
  const [userPoliciesCursor, setUserPoliciesCursor] = useState(null);
  const [isLoadingMoreUsers, setIsLoadingMoreUsers] = useState(false);
  const [isLoadingAccount, setIsLoadingAccount] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [accountForm, setAccountForm] = useState(emptyAccountForm);
  const [isSavingAccount, setIsSavingAccount] = useState(false);

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const loadAccountPolicy = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      setIsLoadingAccount(true);
      setError(null);
      const data = await CreditsEngineService.getAccountPolicy(activeAccountId);
      setAccountPolicy(data);
    } catch (err) {
      setError(err.message || 'No fue posible obtener la política de la cuenta.');
    } finally {
      setIsLoadingAccount(false);
    }
  }, [activeAccountId]);

  // Paginación con cursor del backend: la primera carga trae una página y
  // `loadMore` va apilando las siguientes. El pager numerado opera sobre lo
  // acumulado; useTablePagination llama a `loadMore` cuando el usuario
  // entra a la última página cargada y todavía queda cursor.
  const loadUserPolicies = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      setIsLoadingUsers(true);
      const { items, nextCursor } = await CreditsEngineService.listUserPolicies(activeAccountId);
      setUserPolicies(items);
      setUserPoliciesCursor(nextCursor);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No fue posible obtener las políticas de usuario.', severity: 'error' });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [activeAccountId]);

  const loadMoreUserPolicies = useCallback(async () => {
    if (!activeAccountId || !userPoliciesCursor || isLoadingMoreUsers) return;
    setIsLoadingMoreUsers(true);
    try {
      const { items, nextCursor } = await CreditsEngineService.listUserPolicies(
        activeAccountId, { cursor: userPoliciesCursor }
      );
      setUserPolicies((prev) => [...prev, ...items]);
      setUserPoliciesCursor(nextCursor);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No fue posible obtener más políticas de usuario.', severity: 'error' });
    } finally {
      setIsLoadingMoreUsers(false);
    }
  }, [activeAccountId, userPoliciesCursor, isLoadingMoreUsers]);

  const loadAll = useCallback(() => {
    loadAccountPolicy();
    loadUserPolicies();
  }, [loadAccountPolicy, loadUserPolicies]);

  useEffect(() => {
    if (activeAccountId) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  const openEditAccount = () => {
    setAccountForm({
      active: accountPolicy?.active !== false,
      monthly_credit_limit: accountPolicy?.monthly_credit_limit != null ? String(toNumber(accountPolicy.monthly_credit_limit)) : '',
      daily_credit_limit: accountPolicy?.daily_credit_limit != null ? String(toNumber(accountPolicy.daily_credit_limit)) : '',
      allow_overage: Boolean(accountPolicy?.allow_overage),
      overage_limit: accountPolicy?.overage_limit != null ? String(toNumber(accountPolicy.overage_limit)) : '',
      allow_rollover: Boolean(accountPolicy?.allow_rollover),
      rollover_limit: accountPolicy?.rollover_limit != null ? String(toNumber(accountPolicy.rollover_limit)) : '',
      throughput: accountPolicy?.throughput || {},
      plan_id: accountPolicy?.plan_id || ''
    });
    setAccountFormOpen(true);
  };

  const handleSaveAccount = async () => {
    const payload = {
      account_id: activeAccountId,
      active: accountForm.active,
      allow_overage: accountForm.allow_overage,
      allow_rollover: accountForm.allow_rollover,
      throughput: accountForm.throughput
    };
    if (accountForm.monthly_credit_limit !== '') payload.monthly_credit_limit = Number(accountForm.monthly_credit_limit);
    if (accountForm.daily_credit_limit !== '') payload.daily_credit_limit = Number(accountForm.daily_credit_limit);
    if (accountForm.allow_overage && accountForm.overage_limit !== '') payload.overage_limit = Number(accountForm.overage_limit);
    if (accountForm.allow_rollover && accountForm.rollover_limit !== '') payload.rollover_limit = Number(accountForm.rollover_limit);
    if (accountForm.plan_id.trim()) payload.plan_id = accountForm.plan_id.trim();
    try {
      setIsSavingAccount(true);
      await CreditsEngineService.upsertAccountPolicy(payload);
      setSnackbar({ open: true, message: 'Política de cuenta guardada correctamente.', severity: 'success' });
      setAccountFormOpen(false);
      loadAccountPolicy();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar la política de cuenta.', severity: 'error' });
    } finally {
      setIsSavingAccount(false);
    }
  };

  const openCreateUser = () => {
    setIsEditingUser(false);
    setUserForm(emptyUserForm);
    setUserFormOpen(true);
  };

  const openEditUser = (item) => {
    setIsEditingUser(true);
    setUserForm({
      userId: item.user_id,
      active: item.active !== false,
      monthly_credit_limit: item.monthly_credit_limit != null ? String(toNumber(item.monthly_credit_limit)) : '',
      daily_credit_limit: item.daily_credit_limit != null ? String(toNumber(item.daily_credit_limit)) : '',
      throughput: item.throughput || {}
    });
    setUserFormOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.userId.trim()) {
      setSnackbar({ open: true, message: 'El user_id (Cognito sub) es requerido.', severity: 'error' });
      return;
    }
    const payload = {
      account_id: activeAccountId,
      user_id: userForm.userId.trim(),
      active: userForm.active,
      throughput: userForm.throughput
    };
    if (userForm.monthly_credit_limit !== '') payload.monthly_credit_limit = Number(userForm.monthly_credit_limit);
    if (userForm.daily_credit_limit !== '') payload.daily_credit_limit = Number(userForm.daily_credit_limit);
    try {
      setIsSavingUser(true);
      await CreditsEngineService.upsertUserPolicy(payload);
      setSnackbar({ open: true, message: 'Política de usuario guardada correctamente.', severity: 'success' });
      setUserFormOpen(false);
      loadUserPolicies();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar la política de usuario.', severity: 'error' });
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setIsDeletingUser(true);
      await CreditsEngineService.deleteUserPolicy(activeAccountId, userToDelete.user_id);
      setSnackbar({ open: true, message: `Política de ${userToDelete.user_id} eliminada.`, severity: 'success' });
      setUserToDelete(null);
      loadUserPolicies();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo eliminar la política de usuario.', severity: 'error' });
    } finally {
      setIsDeletingUser(false);
    }
  };

  const { pageRows, paginationProps } = useTablePagination(userPolicies, {
    storageKey: 'billingUserPolicies',
    hasMore: Boolean(userPoliciesCursor),
    isLoadingMore: isLoadingMoreUsers,
    onNeedMore: loadMoreUserPolicies
  });

  return (
    <PageContainer
      title="Política de crédito"
      description="Límites de créditos y throughput por cuenta (tenant) y por usuario. Reemplaza a los antiguos sub-límites de billing-admin."
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadAll} disabled={!activeAccountId}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      {isPlatformRoot && (
        <Section title="Tenant a inspeccionar" subtitle="Como root, puedes ver cualquier tenant escribiendo su account_id.">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              label="account_id"
              value={targetAccountId}
              onChange={(e) => setTargetAccountId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadAll()}
              size="small"
              sx={{ flex: 1, ...darkTextFieldStyle }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="creditos" topic="politica-de-credito" anchor="account_id-solo-root-tenant-a-inspeccionar" />
                  </InputAdornment>
                )
              }}
            />
            <Button variant="contained" onClick={loadAll} disabled={!activeAccountId} sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
              Cargar
            </Button>
          </Stack>
        </Section>
      )}

      {!activeAccountId ? (
        <Alert severity="info">
          {isPlatformRoot
            ? 'Ingresa un account_id para ver su política de crédito.'
            : 'No pudimos determinar tu account_id. Verifica tu sesión.'}
        </Alert>
      ) : (
        <>
          <Section title="Política de cuenta" subtitle={`account_id: ${activeAccountId} — la escritura requiere rol root.`}>
            {isLoadingAccount ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
              </Box>
            ) : (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mb: 2 }}>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Estado: {accountPolicy?.active !== false ? 'Activa' : 'Inactiva'}
                  </Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Límite mensual: {accountPolicy?.monthly_credit_limit != null ? toNumber(accountPolicy.monthly_credit_limit).toFixed(2) : 'Sin límite'}
                  </Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Límite diario: {accountPolicy?.daily_credit_limit != null ? toNumber(accountPolicy.daily_credit_limit).toFixed(2) : 'Sin límite'}
                  </Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Overage: {accountPolicy?.allow_overage ? 'Permitido' : 'No permitido'}
                  </Typography>
                  <Typography sx={{ color: 'var(--wb-text)' }}>
                    Plan: {accountPolicy?.plan_id || '—'}
                  </Typography>
                </Stack>
                {isPlatformRoot && (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={openEditAccount}
                    sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
                    Editar política de cuenta
                  </Button>
                )}
              </>
            )}
          </Section>

          <Section title="Políticas por usuario" subtitle="Root o el admin de esta cuenta pueden crear/editar/eliminar.">
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
              <HelpIcon module="creditos" topic="politica-de-credito" anchor="políticas-por-usuario" />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateUser}
                sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
                Nueva política de usuario
              </Button>
            </Stack>
            {isLoadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
              </Box>
            ) : userPolicies.length === 0 ? (
              <Typography sx={{ color: 'var(--wb-muted-70)', py: 2, textAlign: 'center' }}>
                Ningún usuario de este tenant tiene política propia — todos heredan la política de la cuenta.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>user_id</TableCell>
                      <TableCell sx={headSx}>Estado</TableCell>
                      <TableCell sx={headSx} align="right">Límite mensual</TableCell>
                      <TableCell sx={headSx} align="right">Límite diario</TableCell>
                      <TableCell sx={headSx}>Actualizado</TableCell>
                      <TableCell sx={headSx} align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((item) => (
                      <TableRow key={item.user_id} hover>
                        <TableCell sx={cellSx}>{item.user_id}</TableCell>
                        <TableCell sx={cellSx}>{item.active !== false ? 'Activa' : 'Inactiva'}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          {item.monthly_credit_limit != null ? toNumber(item.monthly_credit_limit).toFixed(2) : 'Sin límite'}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {item.daily_credit_limit != null ? toNumber(item.daily_credit_limit).toFixed(2) : 'Sin límite'}
                        </TableCell>
                        <TableCell sx={cellSx}>{item.updated_at || '—'}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEditUser(item)} sx={{ color: 'var(--wb-blue)' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => setUserToDelete(item)} sx={{ color: 'var(--wb-danger)' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {userPolicies.length > 0 && <TablePaginationBar {...paginationProps} />}
          </Section>
        </>
      )}

      {/* Editar política de cuenta (root únicamente) */}
      <Dialog open={accountFormOpen} onClose={() => !isSavingAccount && setAccountFormOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 520 } } }}>
        <DialogTitle>Editar política de cuenta</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={accountForm.active} onChange={(e) => setAccountForm((prev) => ({ ...prev, active: e.target.checked }))} />}
                label="Cuenta activa"
              />
              <HelpIcon module="creditos" topic="politica-de-credito" anchor="cuenta-activa" />
            </Stack>
            <TextField
              label="Límite mensual de créditos (opcional)"
              value={accountForm.monthly_credit_limit}
              onChange={(e) => setAccountForm((prev) => ({ ...prev, monthly_credit_limit: e.target.value }))}
              type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="creditos" topic="politica-de-credito" anchor="límite-mensual-de-créditos-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Límite diario de créditos (opcional)"
              value={accountForm.daily_credit_limit}
              onChange={(e) => setAccountForm((prev) => ({ ...prev, daily_credit_limit: e.target.value }))}
              type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="creditos" topic="politica-de-credito" anchor="límite-diario-de-créditos-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Plan de suscripción (plan_id, opcional)"
              value={accountForm.plan_id}
              onChange={(e) => setAccountForm((prev) => ({ ...prev, plan_id: e.target.value }))}
              fullWidth size="small" sx={darkTextFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="creditos" topic="politica-de-credito" anchor="plan-de-suscripción-plan_id-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={accountForm.allow_overage} onChange={(e) => setAccountForm((prev) => ({ ...prev, allow_overage: e.target.checked }))} />}
                label="Permitir sobregiro (overage)"
              />
              <HelpIcon module="creditos" topic="politica-de-credito" anchor="permitir-sobregiro-overage" />
            </Stack>
            {accountForm.allow_overage && (
              <TextField
                label="Límite de sobregiro (opcional)"
                value={accountForm.overage_limit}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, overage_limit: e.target.value }))}
                type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpIcon module="creditos" topic="politica-de-credito" anchor="límite-de-sobregiro-opcional" />
                    </InputAdornment>
                  )
                }}
              />
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={accountForm.allow_rollover} onChange={(e) => setAccountForm((prev) => ({ ...prev, allow_rollover: e.target.checked }))} />}
                label="Permitir rollover de créditos no usados"
              />
              <HelpIcon module="creditos" topic="politica-de-credito" anchor="permitir-rollover-de-créditos-no-usados" />
            </Stack>
            {accountForm.allow_rollover && (
              <TextField
                label="Límite de rollover (opcional)"
                value={accountForm.rollover_limit}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, rollover_limit: e.target.value }))}
                type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpIcon module="creditos" topic="politica-de-credito" anchor="límite-de-rollover-opcional" />
                    </InputAdornment>
                  )
                }}
              />
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" sx={{ color: 'var(--wb-muted-80)' }}>
                Límites de throughput
              </Typography>
              <HelpIcon module="creditos" topic="politica-de-credito" anchor="límites-de-throughput-peticiones-por-minutohoradía" />
            </Stack>
            <ThroughputFieldsGrid
              value={accountForm.throughput}
              onChange={(next) => setAccountForm((prev) => ({ ...prev, throughput: next }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountFormOpen(false)} disabled={isSavingAccount}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAccount} disabled={isSavingAccount}
            startIcon={isSavingAccount ? <CircularProgress color="inherit" size={16} /> : null}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crear/editar política de usuario */}
      <Dialog open={userFormOpen} onClose={() => !isSavingUser && setUserFormOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 480 } } }}>
        <DialogTitle>Política de crédito del usuario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="user_id (Cognito sub)"
              value={userForm.userId}
              onChange={(e) => setUserForm((prev) => ({ ...prev, userId: e.target.value }))}
              disabled={isEditingUser}
              fullWidth size="small" sx={darkTextFieldStyle}
            />
            <FormControlLabel
              control={<Switch checked={userForm.active} onChange={(e) => setUserForm((prev) => ({ ...prev, active: e.target.checked }))} />}
              label="Usuario activo"
            />
            <TextField
              label="Límite mensual de créditos (opcional)"
              value={userForm.monthly_credit_limit}
              onChange={(e) => setUserForm((prev) => ({ ...prev, monthly_credit_limit: e.target.value }))}
              type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="creditos" topic="politica-de-credito" anchor="límite-mensual-de-créditos-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label="Límite diario de créditos (opcional)"
              value={userForm.daily_credit_limit}
              onChange={(e) => setUserForm((prev) => ({ ...prev, daily_credit_limit: e.target.value }))}
              type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <HelpIcon module="creditos" topic="politica-de-credito" anchor="límite-diario-de-créditos-opcional" />
                  </InputAdornment>
                )
              }}
            />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" sx={{ color: 'var(--wb-muted-80)' }}>
                Límites de throughput
              </Typography>
              <HelpIcon module="creditos" topic="politica-de-credito" anchor="límites-de-throughput-peticiones-por-minutohoradía" />
            </Stack>
            <ThroughputFieldsGrid
              value={userForm.throughput}
              onChange={(next) => setUserForm((prev) => ({ ...prev, throughput: next }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserFormOpen(false)} disabled={isSavingUser}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveUser} disabled={isSavingUser}
            startIcon={isSavingUser ? <CircularProgress color="inherit" size={16} /> : null}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(userToDelete)} onClose={() => !isDeletingUser && setUserToDelete(null)}>
        <DialogTitle>Eliminar política de usuario</DialogTitle>
        <DialogContent>
          <Typography>¿Confirma eliminar la política de {userToDelete?.user_id}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(null)} disabled={isDeletingUser}>Cancelar</Button>
          <Button color="error" onClick={handleConfirmDeleteUser} disabled={isDeletingUser}
            startIcon={isDeletingUser ? <CircularProgress color="inherit" size={16} /> : null}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const BillingCreditPolicy = () => (
  <RequireBillingRole allow="root-or-admin" title="Política de crédito">
    <BillingCreditPolicyContent />
  </RequireBillingRole>
);

export default BillingCreditPolicy;
